import React, { useState } from 'react';
import { X, Loader2, Check, Plus, Settings as Gear } from 'lucide-react';
import { api, apiError } from './api';

const input = "w-full bg-[#1C1C1E] border border-gray-800 rounded-xl p-3 text-white text-sm focus:outline-none focus:border-[#00BFFF]";
const lbl = "text-[10px] text-gray-500 uppercase tracking-wider";

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold">{title}</p>
    {children}
  </div>
);

export default function SettingsModal({ t, onClose, onSaved }) {
  const isEvent = !!t.event_id;
  const [f, setF] = useState({
    name: t.name || '', location: t.location || '', start_date: t.start_date || '', end_date: t.end_date || '',
    referee_count: t.referee_count ?? 2, match_periods: t.match_periods ?? 2, match_period_length: t.match_period_length ?? 45,
    match_length: t.match_length ?? 50, match_gap: t.match_gap ?? 5, start_time: t.start_time || '',
    pitchesText: (t.pitches || []).join(', '), final_name: t.final_name || 'Final',
    knockout_qualifiers: t.knockout_qualifiers || 0, seed_mode: t.seed_mode || 'cross',
    nearest_hospital: t.nearest_hospital || '', hospital_route: t.hospital_route || '', first_aider: t.first_aider || '', emergency_phone: t.emergency_phone || '',
    safeguarding_officer: t.safeguarding_officer || '', safeguarding_phone: t.safeguarding_phone || '', safeguarding_email: t.safeguarding_email || '', safeguarding_notes: t.safeguarding_notes || '',
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  const [div, setDiv] = useState({ age_group: '', gender: 'mixed', standard: '', start_time: '', pitchesText: '' });
  const [divBusy, setDivBusy] = useState(false);
  const [divMsg, setDivMsg] = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api.patch(`/tournaments/${t.id}`, {
        name: f.name, location: f.location, start_date: f.start_date, end_date: f.end_date,
        referee_count: Number(f.referee_count) || 1, match_periods: Number(f.match_periods) || 1,
        match_period_length: Number(f.match_period_length) || 1, match_length: Number(f.match_length) || 1,
        match_gap: Number(f.match_gap) || 0, start_time: f.start_time,
        pitches: f.pitchesText.split(',').map((s) => s.trim()).filter(Boolean),
        final_name: f.final_name, knockout_qualifiers: Number(f.knockout_qualifiers) || 0, seed_mode: f.seed_mode,
        nearest_hospital: f.nearest_hospital, hospital_route: f.hospital_route, first_aider: f.first_aider, emergency_phone: f.emergency_phone,
        safeguarding_officer: f.safeguarding_officer, safeguarding_phone: f.safeguarding_phone, safeguarding_email: f.safeguarding_email, safeguarding_notes: f.safeguarding_notes,
      });
      setSaved(true); setTimeout(() => setSaved(false), 1800);
      onSaved && onSaved();
    } catch (e) { setErr(apiError(e)); }
    setBusy(false);
  };

  const addDivision = async () => {
    setDivBusy(true); setDivMsg('');
    try {
      const { data } = await api.post(`/tournaments/${t.id}/divisions`, {
        age_group: div.age_group, gender: div.gender, standard: div.standard, start_time: div.start_time,
        pitches: div.pitchesText.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setDivMsg(`Added ${data.name}`);
      setDiv({ age_group: '', gender: 'mixed', standard: '', start_time: '', pitchesText: '' });
      onSaved && onSaved();
    } catch (e) { setDivMsg(apiError(e)); }
    setDivBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 flex items-stretch justify-end" data-testid="settings-modal" onClick={onClose}>
      <div className="bg-[#0F0F0F] border-l border-gray-800 w-full max-w-md h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-gray-800 px-5 py-4 flex items-center justify-between z-10">
          <h2 className="text-base font-black flex items-center gap-2"><Gear size={18} /> Tournament settings</h2>
          <button data-testid="settings-close" onClick={onClose} className="text-gray-500 hover:text-white"><X size={22} /></button>
        </div>

        <div className="p-5 space-y-6">
          <Section title="Details">
            <div><label className={lbl}>Name</label><input data-testid="set-name" className={input + ' mt-1'} value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div><label className={lbl}>Location</label><input data-testid="set-location" className={input + ' mt-1'} value={f.location} onChange={(e) => set('location', e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Start date</label><input type="date" className={input + ' mt-1'} value={f.start_date} onChange={(e) => set('start_date', e.target.value)} /></div>
              <div><label className={lbl}>End date</label><input type="date" className={input + ' mt-1'} value={f.end_date} onChange={(e) => set('end_date', e.target.value)} /></div>
            </div>
          </Section>

          <Section title="Scheduling">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Referees</label><input type="number" min="1" data-testid="set-referees" className={input + ' mt-1'} value={f.referee_count} onChange={(e) => set('referee_count', e.target.value)} /></div>
              <div><label className={lbl}>Periods</label><input type="number" min="1" max="4" className={input + ' mt-1'} value={f.match_periods} onChange={(e) => set('match_periods', e.target.value)} /></div>
              <div><label className={lbl}>Period length (mins)</label><input type="number" min="1" className={input + ' mt-1'} value={f.match_period_length} onChange={(e) => set('match_period_length', e.target.value)} /></div>
              <div><label className={lbl}>Match length (mins)</label><input type="number" min="1" data-testid="set-match-length" className={input + ' mt-1'} value={f.match_length} onChange={(e) => set('match_length', e.target.value)} /></div>
              <div><label className={lbl}>Gap (mins)</label><input type="number" min="0" className={input + ' mt-1'} value={f.match_gap} onChange={(e) => set('match_gap', e.target.value)} /></div>
              <div><label className={lbl}>Start time</label><input type="time" className={input + ' mt-1'} value={f.start_time} onChange={(e) => set('start_time', e.target.value)} /></div>
            </div>
            <div><label className={lbl}>Pitches</label><input data-testid="set-pitches" className={input + ' mt-1'} placeholder="Pitch 1, Pitch 2" value={f.pitchesText} onChange={(e) => set('pitchesText', e.target.value)} /></div>
            <p className="text-[11px] text-gray-600">Changing scheduling won't reshuffle existing fixtures — regenerate fixtures to apply new times/pitches.</p>
          </Section>

          {t.format !== 'league' && (
            <Section title="Finals">
              <div><label className={lbl}>Name of the main final</label><input data-testid="set-final-name" className={input + ' mt-1'} value={f.final_name} onChange={(e) => set('final_name', e.target.value)} /></div>
              {t.format === 'groups_knockout' && (
                <div>
                  <label className={lbl}>Who qualifies to the knockout?</label>
                  <select data-testid="set-qualifiers" className={input + ' mt-1'} value={f.seed_mode === 'overall' ? String(f.knockout_qualifiers) : 'cross'}
                    onChange={(e) => { const v = e.target.value; if (v === 'cross') setF((p) => ({ ...p, seed_mode: 'cross', knockout_qualifiers: 0 })); else setF((p) => ({ ...p, seed_mode: 'overall', knockout_qualifiers: Number(v) })); }}>
                    <option value="cross">Top 2 of each group — Winner A v Runner-up B</option>
                    <option value="2">Top 2 overall → Final (1st v 2nd)</option>
                    <option value="4">Top 4 overall → Semi-finals (1v4, 2v3)</option>
                    <option value="8">Top 8 overall → Quarter-finals</option>
                    <option value="16">Top 16 overall → Round of 16</option>
                  </select>
                </div>
              )}
            </Section>
          )}

          <Section title="Emergency & medical">
            <input data-testid="set-hospital" className={input} placeholder="Nearest hospital" value={f.nearest_hospital} onChange={(e) => set('nearest_hospital', e.target.value)} />
            <textarea rows={2} className={input} placeholder="Route / directions" value={f.hospital_route} onChange={(e) => set('hospital_route', e.target.value)} />
            <input className={input} placeholder="First aider on site" value={f.first_aider} onChange={(e) => set('first_aider', e.target.value)} />
            <input className={input} placeholder="Emergency contact number" value={f.emergency_phone} onChange={(e) => set('emergency_phone', e.target.value)} />
          </Section>

          <Section title="Safeguarding">
            <input className={input} placeholder="Safeguarding officer" value={f.safeguarding_officer} onChange={(e) => set('safeguarding_officer', e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <input className={input} placeholder="Phone" value={f.safeguarding_phone} onChange={(e) => set('safeguarding_phone', e.target.value)} />
              <input className={input} placeholder="Email" value={f.safeguarding_email} onChange={(e) => set('safeguarding_email', e.target.value)} />
            </div>
            <textarea rows={2} className={input} placeholder="How to raise a concern" value={f.safeguarding_notes} onChange={(e) => set('safeguarding_notes', e.target.value)} />
          </Section>

          {err && <p className="text-[#FF3B30] text-sm">{err}</p>}
          <button data-testid="settings-save" onClick={save} disabled={busy}
            className="w-full bg-[#00BFFF] text-black font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : saved ? <Check size={16} /> : null}{busy ? 'Saving…' : saved ? 'Saved' : 'Save settings'}
          </button>

          <div className="border-t border-gray-800 pt-5">
            <Section title="Add a division">
              <p className="text-[11px] text-gray-500">{isEvent ? 'Add another division to this event day.' : 'Adding a division turns this into a multi-division event day.'}</p>
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="div-age" className={input} placeholder="U9" value={div.age_group} onChange={(e) => setDiv({ ...div, age_group: e.target.value })} />
                <select className={input} value={div.gender} onChange={(e) => setDiv({ ...div, gender: e.target.value })}>
                  <option value="mixed">Mixed</option><option value="girls">Girls</option>
                </select>
                <input className={input} placeholder="Standard" value={div.standard} onChange={(e) => setDiv({ ...div, standard: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="time" className={input} value={div.start_time} onChange={(e) => setDiv({ ...div, start_time: e.target.value })} />
                <input className={input} placeholder="Pitches" value={div.pitchesText} onChange={(e) => setDiv({ ...div, pitchesText: e.target.value })} />
              </div>
              {divMsg && <p className="text-xs text-[#00D26A]">{divMsg}</p>}
              <button data-testid="add-division-settings" onClick={addDivision} disabled={divBusy}
                className="w-full bg-[#1C1C1E] border border-[#00BFFF]/50 text-[#00BFFF] font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50">
                {divBusy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add division
              </button>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
